import React from "react"
import { Modal, Form, Col, Button } from "react-bootstrap"
import { FaAngleDown, FaPencilAlt, FaTimes } from "react-icons/fa"
import { toast } from "react-toastify"
import { concat, of, Subject, Subscription } from "rxjs"
import { catchError, debounceTime, filter, map, switchMap } from "rxjs/operators"
import SendingFacilityRecord from "../../api/Administration/Model/SendingFacilityRecord"
import Timezone from "../../api/Administration/Model/Timezone"
import { APIError } from "../../api/ObservableFromFetch"
import AutoFilterPortalsGetObservable from "../../api/Portals/AutoFilterPortalsGet"
import PortalData from "../../api/Portals/PortalModel/PortalData"
import PortalsGetObservable, { PortalGetObservable } from "../../api/Portals/PortalsGet"
import ActionButton from "../ActionButton"
import { AsyncTypeahead, Typeahead } from "react-bootstrap-typeahead"
import ToggleSwitch from "../FormattedInputs/ToggleSwitch"
import { SaveSendingFacilityObservable } from "../../api/Administration/SendingFacilities"

interface SendingFacilityModalProps {
  addNew?: boolean
  timezones: Timezone[]
  details?: SendingFacilityRecord
  onHide: () => void
  onSave: () => void
}

interface SendingFacilityModalState {
  isSendingFacilityValid: boolean,
  loading: boolean
  saving: boolean
  editing: boolean
  currentPortal?: PortalData
  initialPortalData?: PortalData
  retrievedPortalPredictions?: string[]
  retrievedPortals?: PortalData[]
  SendingFacility: string
  WS_Url?: string
  UserName?: string
  Password?: string
  SOAPVersion?: string
  Namespace?: string
  SendingFacility_Code?: string
  TimeZoneAN?: number
  WSUserName?: string
  WSPassword?: string
  MaskPos?: boolean
  SendImages?: boolean
  ImplementationID?: number
  CRMUpdates?: boolean
  InstallationId?: string
}

export default class SendingFacilityModal extends React.PureComponent<SendingFacilityModalProps, SendingFacilityModalState> {
  state: SendingFacilityModalState = {
    loading: false,
    saving: false,
    editing: false,
    SendingFacility: "",
    isSendingFacilityValid: true
  }

  subscription?: Subscription
  loadSubscription?: Subscription
  saveSubscription?: Subscription
  loadPortalNameSubscription?: Subscription
  implementationName$ = new Subject<string>()
  searchImplementation$ = new Subject<string>()
  timezoneTypeahead = React.createRef<Typeahead<Timezone>>()
  portalTypeahead = React.createRef<AsyncTypeahead<string>>()

  componentDidMount() {
    this.setState({...this.defaultStateFromProps()})

    this.subscription = this.searchImplementation$.pipe(
      debounceTime(500),
      filter(name => name.length >= 3),
      map(name => name.trim()),
      switchMap(name => concat(
        of({ loading: true }),
        AutoFilterPortalsGetObservable(name).pipe(
          map(result => ({ loading: false, result: result })),
          catchError(() => of({ loading: false }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: string[] }) => this.setState({ loading: o.loading, retrievedPortalPredictions: o.result }),
      (error) => this.handleAPIError(error, { loading: false })
    )

    this.loadSubscription = this.implementationName$.pipe(
      map(name => name.trim()),
      switchMap(name => concat(
        of({ loading: true }),
        PortalsGetObservable(name).pipe(
          map(result => ({ loading: false, result: result })),
          catchError(() => of({ loading: false }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: PortalData[] }) => this.setState({
        loading: o.loading,
        retrievedPortals: o.result && o.result.length > 1 ? o.result : undefined,
        ImplementationID: o.result && o.result.length === 1 ? o.result[0].ImplementationID : undefined,
        currentPortal: o.result && o.result.length === 1 ? o.result[0] : undefined
      }),
      (error) => this.handleAPIError(error, { loading: false })
    )

    if (this.props.details?.ImplementationID) {
      this.loadPortalNameSubscription = PortalGetObservable(this.props.details?.ImplementationID).subscribe(
        data  => this.setState({initialPortalData: data, currentPortal: data}),
        error => this.handleAPIError(error, {})
      )
    }
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.loadPortalNameSubscription && this.loadPortalNameSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  onCancel = () => this.props.onHide()

  onSubmit = () => {
    this.setState({saving: true})
    const data: SendingFacilityRecord = {
      SendingFacilityAN: this.props.details?.SendingFacilityAN,
      SendingFacility: this.state.SendingFacility,
      WS_Url: this.state.WS_Url,
      UserName: this.state.UserName,
      Password: this.state.Password,
      SOAPVersion: this.state.SOAPVersion,
      Namespace: this.state.Namespace,
      SendingFacility_Code: this.state.SendingFacility_Code,
      TimeZoneAN: this.state.TimeZoneAN,
      WSUserName: this.state.WSUserName,
      WSPassword: this.state.WSPassword,
      MaskPos: this.state.MaskPos,
      SendImages: this.state.SendImages,
      ImplementationID: this.state.ImplementationID,
      CRMUpdates: this.state.CRMUpdates,
      InstallationId: this.state.InstallationId
    }

    this.saveSubscription = SaveSendingFacilityObservable(data).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
        this.props.onSave()
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  timezoneValue = () => this.props.timezones.find(zone => zone.TimeZoneID === this.state.TimeZoneAN) || undefined
  valueText = (text: string | number | undefined) => <Col xs={6} md={8} className="col-form-label">{text}</Col>
  rowClassName = (required: boolean = false) => required ? "form-group required" : "form-group"

  isValid = () => this.state.isSendingFacilityValid && this.state.SendingFacility.length > 0

  handleToggleEditMode = () => {
    this.setState({editing: !this.state.editing, ...this.defaultStateFromProps()})
  }

  defaultStateFromProps = () => {
    const data = this.props.details
    return {
      SendingFacility: data?.SendingFacility || "",
      WS_Url: data?.WS_Url,
      UserName: data?.UserName,
      Password: undefined,
      SOAPVersion: data?.SOAPVersion,
      Namespace: data?.Namespace,
      SendingFacility_Code: data?.SendingFacility_Code,
      TimeZoneAN: data?.TimeZoneAN,
      WSUserName: data?.WSUserName,
      WSPassword: undefined,
      MaskPos: data?.MaskPos,
      SendImages: data?.SendImages,
      ImplementationID: data?.ImplementationID,
      CRMUpdates: data?.CRMUpdates,
      InstallationId: data?.InstallationId,
      currentPortal: this.state.initialPortalData
    }
  }

  handleSendingFacilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.setState({ SendingFacility: value, isSendingFacilityValid: value.length >= 1 })
  }

  handleImplementationChange = (selected: string[]) => {
    if (selected[0]) {
      this.implementationName$.next(selected[0])
    }
    else {
      this.setState({currentPortal: undefined, ImplementationID: undefined})
    }
  }

  render() {
    const { loading, saving, currentPortal, SendingFacility, MaskPos, WS_Url, UserName, SOAPVersion,
      Namespace, SendingFacility_Code, WSUserName, SendImages, CRMUpdates, InstallationId, Password, WSPassword } = this.state
    const editing = this.state.editing || !!this.props.addNew
    const timezone = this.timezoneValue()

    return (
      <Modal backdrop="static" show size="lg" dialogClassName="dark">
        <Modal.Header>
          {!!this.props.addNew && <Modal.Title className="text-uppercase">Add New Sending Facility</Modal.Title>}
          {!editing && <Modal.Title className="text-uppercase">Sending Facility</Modal.Title>}
          {editing && !this.props.addNew && <Modal.Title className="text-uppercase">Edit Sending Facility</Modal.Title>}
        </Modal.Header>
        <Modal.Body>
          <Form className="px-4">
            {!this.props.addNew && <Form.Row>
              <Col><h5>MODIFY SENDING FACILITY</h5></Col>
              <Col sm={1} className={"text-right"}>
                <h5>
                  <Button variant="icon-link" size="sm" className="float-right mb-3 btn-icon-primary" onClick={this.handleToggleEditMode}>
                    {!this.state.editing && <FaPencilAlt />}
                    {this.state.editing && <FaTimes />}
                  </Button>
                </h5>
              </Col>
            </Form.Row>}
            <Form.Row className={this.rowClassName(editing)}>
              <Form.Label column xs={6} md={4}>Sending Facility</Form.Label>
              {!editing && this.valueText(SendingFacility)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={SendingFacility} maxLength={50} onChange={this.handleSendingFacilityChange} isInvalid={!this.state.isSendingFacilityValid} />
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Namespace</Form.Label>
              {!editing && this.valueText(Namespace)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={Namespace} maxLength={255} onChange={e => this.setState({ Namespace: e.target.value })} />
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Code</Form.Label>
              {!editing && this.valueText(SendingFacility_Code)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={SendingFacility_Code} maxLength={15} onChange={e => this.setState({ SendingFacility_Code: e.target.value })} />
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>WS Url</Form.Label>
              {!editing && this.valueText(WS_Url)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={WS_Url} maxLength={255} onChange={e => this.setState({ WS_Url: e.target.value })} />
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>UserName</Form.Label>
              {!editing && this.valueText(UserName)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={UserName} maxLength={50} onChange={e => this.setState({ UserName: e.target.value })} />
                </Col>}
            </Form.Row>
            {editing && <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Password</Form.Label>
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={Password} placeholder={this.props.addNew ? "" : "Enter new password to update existing"} maxLength={50} onChange={e => this.setState({ Password: e.target.value })} />
                </Col>
            </Form.Row>}
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>WS UserName</Form.Label>
              {!editing && this.valueText(WSUserName)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={WSUserName} maxLength={50} onChange={e => this.setState({ WSUserName: e.target.value })} />
                </Col>}
            </Form.Row>
            {editing && <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>WS Password</Form.Label>
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={WSPassword} placeholder={this.props.addNew ? "" : "Enter new password to update existing"} maxLength={50} onChange={e => this.setState({ WSPassword: e.target.value })} />
                </Col>
            </Form.Row>}
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Timezone</Form.Label>
              {!editing && this.valueText(timezone?.Description)}
              {editing &&
                <Col xs={6} md={8}>
                  <Typeahead id="timezone"
                    ref={this.timezoneTypeahead}
                    labelKey="Description"
                    filterBy={() => true}
                    onChange={(items: Timezone[]) => this.setState({TimeZoneAN: items[0]?.TimeZoneID || undefined})}
                    options={this.props.timezones}
                    selected={timezone ? [timezone] : []}
                    emptyLabel="No Timezone"
                    placeholder="Select">
                    <Button variant="toggle" onClick={() => { this.timezoneTypeahead && this.timezoneTypeahead.current && (this.timezoneTypeahead.current as any).toggleMenu() }}>
                      <FaAngleDown />
                    </Button>
                  </Typeahead>
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Implementation</Form.Label>
              {!editing && this.valueText(currentPortal?.CompanyName)}
              {editing &&
                <Col xs={6} md={8}>
                  <AsyncTypeahead id="implementation"
                    ref={this.portalTypeahead}
                    filterBy={() => true}
                    isLoading={loading}
                    onSearch={() => { }}
                    onInputChange={input => this.searchImplementation$.next(input)}
                    onChange={selected => this.handleImplementationChange(selected)}
                    options={this.state.retrievedPortalPredictions || []}
                    selected={currentPortal ? [currentPortal.CompanyName || ""] : []}
                    minLength={0}
                    emptyLabel="No Portals Found"
                    placeholder=""
                    inputProps={{ autoComplete: "chrome-off" }}>
                    {!loading &&
                      <Button variant="toggle" onClick={() => { this.portalTypeahead && this.portalTypeahead.current && (this.portalTypeahead.current as any).toggleMenu() }}>
                        <FaAngleDown />
                      </Button>}
                  </AsyncTypeahead>
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Mask Pos</Form.Label>
              <Col xs={6} md={8}>
                <ToggleSwitch className="ml-0 mt-2" on="Yes" off="No" disabled={!editing} checked={MaskPos} onCheck={() => this.setState({MaskPos: !this.state.MaskPos})} />
              </Col>
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Send Images</Form.Label>
              <Col xs={6} md={8}>
                <ToggleSwitch className="ml-0 mt-2" on="Yes" off="No" disabled={!editing} checked={SendImages} onCheck={() => this.setState({SendImages: !this.state.SendImages})} />
              </Col>
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>CRM Updates</Form.Label>
              <Col xs={6} md={8}>
                <ToggleSwitch className="ml-0 mt-2" on="Yes" off="No" disabled={!editing} checked={CRMUpdates} onCheck={() => this.setState({CRMUpdates: !this.state.CRMUpdates})} />
              </Col>
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>SOAP Version</Form.Label>
              {!editing && this.valueText(SOAPVersion)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={SOAPVersion} maxLength={2} onChange={e => this.setState({ SOAPVersion: e.target.value })} />
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Installation Id</Form.Label>
              {!editing && this.valueText(InstallationId)}
              {editing &&
                <Col xs={6} md={8}>
                  <Form.Control type="text" value={InstallationId} maxLength={50} onChange={e => this.setState({ InstallationId: e.target.value })} />
                </Col>}
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={ this.onCancel } className="px-4">{editing ? "Cancel" : "Close"}</Button>
          {editing && <ActionButton variant="primary" onClick={ this.onSubmit } disabled={loading || !this.isValid()} spin={saving} disableOnSpin>{!!this.props.addNew ? "Save" : "Update"}</ActionButton>}
        </Modal.Footer>
      </Modal>
    )
  }
}
